
import { supabase } from '@/integrations/supabase/client';
import { getProfile } from '@/services/profileService';

export const signIn = async (email: string, password: string) => {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { error };
};

export const signOut = async () => {
  try {
    console.log('Starting sign out process...');
    
    // Always clear local session first to prevent multiple logout attempts
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    
    if (error) {
      console.warn('Local signOut had issues (continuing anyway):', error);
    } else {
      console.log('Successfully cleared local session');
    }
    
    // Try global signout (from all devices) but don't block on errors
    try {
      await supabase.auth.signOut({ scope: 'global' });
      console.log('Successfully signed out globally');
    } catch (globalError) {
      console.warn('Global signout failed (not blocking):', globalError);
    }
    
  } catch (error) {
    console.error('Sign out error (continuing anyway):', error);
  } finally {
    // Always force navigation regardless of Supabase response
    // Use replace to prevent back button issues
    console.log('Forcing navigation to login page...');
    window.location.replace('/admin/login');
  }
};

export const resetPassword = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return { error };
};

export const changePassword = async (newPassword: string) => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });
  return { error };
};

export const updateProfile = async (userId: string, updates: { full_name?: string }) => {
  try {
    // First try to update in platform_admins table
    const { data: platformAdmin } = await supabase
      .from('platform_admins')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (platformAdmin) {
      const { error: platformError } = await supabase
        .from('platform_admins')
        .update({
          full_name: updates.full_name,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (platformError) {
        return { error: platformError };
      }
    } else {
      // Try to update in organization_members table
      const { error: orgError } = await supabase
        .from('organization_members')
        .update({
          full_name: updates.full_name,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (orgError) {
        return { error: orgError };
      }
    }

    // Refresh profile data
    const updatedProfile = await getProfile(userId);
    return { error: null, profile: updatedProfile };
  } catch (error) {
    return { error };
  }
};

export const updateInvitationStatusOnSignIn = async (userId: string) => {
  try {
    // Try to update in platform_admins first
    const { error: platformError } = await supabase
      .from('platform_admins')
      .update({ 
        invitation_status: 'accepted',
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .eq('invitation_status', 'pending');

    // Try to update in organization_members
    const { error: orgError } = await supabase
      .from('organization_members')
      .update({ 
        invitation_status: 'accepted',
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .eq('invitation_status', 'pending');

    if (!platformError || !orgError) {
      console.log('✅ Updated invitation status to accepted for user:', userId);
    }
  } catch (error) {
    console.warn('Error updating invitation status:', error);
  }
};
